import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Shield, Heart, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import PracticeScenario from '../shared/PracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function FearOfFeelings() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  const fears = [
    {
      fear: 'Fear of being overwhelmed',
      belief: '"If I start crying, I\'ll never stop"',
      truth: 'Feelings are like waves—they rise, peak, and fall. No feeling lasts forever.',
      icon: '🌊'
    },
    {
      fear: 'Fear of losing control',
      belief: '"If I feel my anger, I might hurt someone"',
      truth: 'Feeling an emotion is different from acting on it. You can feel without acting.',
      icon: '🎭'
    },
    {
      fear: 'Fear of vulnerability',
      belief: '"Showing feelings means I\'m weak"',
      truth: 'Vulnerability takes courage. It\'s the birthplace of connection.',
      icon: '💪'
    },
    {
      fear: 'Fear of judgment',
      belief: '"People will think less of me"',
      truth: 'Authentic emotion often inspires trust and deeper connection.',
      icon: '👀'
    },
    {
      fear: 'Fear of the unknown',
      belief: '"I don\'t know what I\'ll find"',
      truth: 'What we resist persists. What we feel, we can heal.',
      icon: '❓'
    }
  ];

  const strategies = [
    {
      title: 'Start Small',
      description: 'Begin with less intense feelings. Build your "emotional fitness" gradually.'
    },
    {
      title: 'Create Safety',
      description: 'Choose a safe time and place. Consider working with a trusted person or therapist.'
    },
    {
      title: 'Use the Body',
      description: 'Notice where feelings live in your body. This creates helpful distance while staying connected.'
    },
    {
      title: 'Set Boundaries',
      description: 'You can explore feelings in "doses." It\'s okay to take breaks and return later.'
    },
    {
      title: 'Self-Compassion',
      description: 'Speak to yourself as you would a dear friend. "It makes sense you feel this way."'
    },
    {
      title: 'Ground Yourself',
      description: 'Feel your feet on the floor. Notice 5 things you can see. Come back to present.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Workbook
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">The Fear of Feeling Our Feelings</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 57</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Shield className="h-16 w-16 text-blue-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">The Fear of Feeling Our Feelings</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">Understanding why we resist emotions—and how to safely befriend them.</p>
          </div>

          <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-xl font-bold text-red-200 mb-4 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Why We Avoid Our Feelings
            </h3>
            <p className="text-red-100 mb-4">Many of us learned early that some feelings weren\'t safe or acceptable. We developed protective strategies that once served us but may now limit our aliveness.</p>
            <p className="text-slate-300 italic">"The only way out is through." — Robert Frost</p>
          </div>

          <h3 className="text-2xl font-bold text-white mb-6">Common Fears and Reframes</h3>
          <div className="space-y-4 mb-10">
            {fears.map((item, index) => (
              <div key={index} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{item.icon}</div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-amber-400 mb-1">{item.fear}</h4>
                    <p className="text-slate-400 text-sm italic mb-2">"{item.belief}"</p>
                    <div className="bg-green-900/30 rounded-lg p-3">
                      <p className="text-green-200"><strong>The Truth:</strong> {item.truth}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-xl font-bold text-purple-200 mb-4 flex items-center gap-2">
              <Heart className="h-5 w-5" /> Strategies for Befriending Feelings
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {strategies.map((strategy, index) => (
                <div key={index} className="bg-slate-900/50 rounded-lg p-4">
                  <h4 className="font-bold text-purple-200 mb-1">{strategy.title}</h4>
                  <p className="text-slate-300 text-sm">{strategy.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-lg font-bold text-blue-200 mb-3">💙 Remember</h3>
            <p className="text-blue-100">"You are not your feelings. You have feelings. They are important messengers, pointing to your needs. By listening to them with curiosity and compassion, you honor your full humanity."</p>
          </div>

          <PracticeScenario pageTitle="The Fear of Feeling Our Feelings" pageNumber={57} />
          <PageMessaging pageTitle="The Fear of Feeling Our Feelings" pageNumber={57} />
        </div>
      </div>
    </div>
  );
}
